<script>
	import { scale } from 'svelte/transition';
	import { closeModal } from '$lib/modal-store';
	import KeyboardShortcut from '../smol_ui/keyboardShortcut.svelte';
	import { t } from '$lib/i18n.svelte.js';

	/**
	 * @typedef {Object} Props
	 * @property {string} title - Modal title
	 * @property {string} content - Main content text
	 * @property {string} [subtext] - Optional subtext
	 * @property {boolean} [infoMode] - Info mode (only OK button)
	 * @property {string} [illustration] - Optional icon path for illustration
	 * @property {string} [confirmText] - Custom confirm button text
	 * @property {() => void} confirmCallback - Confirm action callback
	 * @property {() => void} [cancelCallback] - Cancel action callback
	 */

	/** @type {Props} */
	let { 
		title, 
		content, 
		subtext = '', 
		infoMode = false, 
		illustration = '', 
		confirmText = '',
		confirmCallback, 
		cancelCallback = () => {} 
	} = $props();

	/** @type {string} */
	let confirmButtonText = $derived(
		infoMode ? 'Ok' : 
		confirmText || 
		t('info.confirm', { defaultValue: 'Confirm' })
	);

	/**
	 * @param {KeyboardEvent} event
	 */
	function handleKeyDown(event) {
		if (event.key === 'Escape') {
			cancelCallback();
			closeModal();
			event.preventDefault();
			event.stopPropagation();
		} else if (event.key === 'Enter' && !infoMode) {
			confirmCallback();
			closeModal();
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
		}
	}

	function handleConfirm() {
		if (!infoMode) confirmCallback();
		closeModal();
	}

	function handleCancel() {
		cancelCallback();
		closeModal();
	}

	$effect(() => {
		window.addEventListener('keydown', handleKeyDown);
		document.querySelector('.confirmButton')?.focus();
		
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	});
</script>

<div
	role="dialog"
	class="modal z-40"
	in:scale|global={{ duration: 300, start: 0.95 }}
	out:scale|global={{ duration: 300, start: 0.95 }}
>
	<div class="pointer-events-auto z-40 mx-4 w-[90%] max-w-[480px] bg-white dark:bg-ord-cardcolor-dark rounded-2xl overflow-hidden shadow-lg border border-gray-100 dark:border-ord-border-dark flex flex-col">
		
		{#if illustration}
			<div class="relative py-8 px-8 flex justify-center bg-gradient-to-br from-blue-50 to-blue-100 dark:from-gray-800 dark:to-gray-900 overflow-hidden">
				<div class="absolute w-[150px] h-[150px] rounded-full bg-gradient-to-br from-blue-500/10 to-blue-500/20 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></div>
				<div class="relative z-10 bg-white dark:bg-gray-700 rounded-full w-16 h-16 flex items-center justify-center shadow-md">
					<img 
						src={illustration}
						alt="modal illustration" 
						class="w-8 h-8 opacity-85 dark:invert"
					/>
				</div>
			</div>
		{/if}
		
		<div class="px-6 pt-6 pb-4">
			<h3 class="text-xl font-semibold mb-3 text-gray-900 dark:text-white text-left">
				{title}
			</h3>
			
			<div class="text-left">
				<p class="text-sm leading-relaxed text-gray-600 dark:text-gray-300 mb-2">
					{content}
				</p>
				{#if subtext}
					<p class="text-xs text-gray-500 dark:text-gray-400">
						{subtext}
					</p>
				{/if}
			</div>
		</div>
		
		<div class="flex justify-end gap-3 px-6 pb-6">
			{#if !infoMode}
				<button 
					class="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ord-button hvr-grow flex items-center"
					onclick={handleCancel}
				>
					{t('info.cancel', { defaultValue: 'Cancel' })}
					<KeyboardShortcut shortcut="Esc" />
				</button>
			{/if}
			
			<button 
				class="confirmButton px-4 py-2 text-sm font-medium rounded-lg bg-royal-blue text-white hover:bg-royal-blue/80 transition-colors ord-button hvr-grow flex items-center shadow-xs"
				onclick={handleConfirm}
			>
				{confirmButtonText}
				<KeyboardShortcut shortcut="⏎" />
			</button>
		</div>
	</div>
</div>

<style>
	.modal {
		position: fixed;
		top: 0;
		bottom: 0;
		right: 0;
		left: 0;
		display: flex;
		justify-content: center;
		align-items: center;
		pointer-events: none;
	}
</style>
